import React, { useState, useEffect } from 'react';
import { 
  Button, Spinner, Badge
} from '../ui/UIComponents';
import { Role, Permission } from '../../types/rbac.types';
import { getPermissions, addPermissionToRole, removePermissionFromRole, getApplications, addPermissionsToRoleBulk, removePermissionsFromRoleBulk } from '../../services/rbacApi';
import { Search, Layers, FolderKanban } from 'lucide-react';
import PermissionTreeView, { PermissionTreeNode } from './PermissionTreeView';

interface RolePermissionsProps {
  isOpen: boolean;
  onClose: (refreshData?: boolean) => void;
  role: Role;
}

const RolePermissions: React.FC<RolePermissionsProps> = ({ isOpen, onClose, role }) => {
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  
  // Fetch permissions on component mount
  useEffect(() => {
    if (isOpen) {
      fetchPermissions();
    }
  }, [isOpen]);
  
  const fetchPermissions = async () => {
    try {
      setLoading(true);
      
      // Get all applications first
      const apps = await getApplications();
      setApplications(apps);
      
      let allPermissions: Permission[] = [];
      
      // If the role is tied to a specific application, get permissions for that app
      if (role.application_id) {
        allPermissions = await getPermissions(role.application_id);
      } else {
        // For global roles, get permissions from all applications
        for (const app of apps) {
          const appPermissions = await getPermissions(app.id);
          allPermissions = [...allPermissions, ...appPermissions];
        }
      }
      
      // If role has permissions, separate them
      if (role.permissions && role.permissions.length > 0) {
        const rolePermIds = role.permissions.map(p => p.id);
        const assigned = allPermissions.filter(p => rolePermIds.includes(p.id));
        const available = allPermissions.filter(p => !rolePermIds.includes(p.id));
        
        setRolePermissions(assigned);
        setAvailablePermissions(available);
      } else {
        setAvailablePermissions(allPermissions);
        setRolePermissions([]);
      }
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch permissions');
      console.error('Error fetching permissions:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddPermission = async (permission: Permission) => {
    try {
      setIsSubmitting(true);
      await addPermissionToRole(role.id, permission.id);
      
      // Update local state
      setAvailablePermissions(prev => prev.filter(p => p.id !== permission.id));
      setRolePermissions(prev => [...prev, permission]);
      setHasChanges(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add permission');
      console.error('Error adding permission:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPermissionsBulk = async (permissions: Permission[]) => {
    if (!permissions || permissions.length === 0) return;
    try {
      setIsSubmitting(true);
      const ids = permissions.map(p => p.id);
      const res = await addPermissionsToRoleBulk(role.id, ids);
      // Refresh local lists from result.role to keep in sync
      const updatedIds = new Set(res.role.permissions?.map(p => p.id) || []);
      setRolePermissions(res.role.permissions || []);
      // Recompute available by filtering from all available + current available
      setAvailablePermissions(prev => prev.filter(p => !updatedIds.has(p.id)));
      setHasChanges(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add permissions');
      console.error('Error adding permissions bulk:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleRemovePermission = async (permission: Permission) => {
    try {
      setIsSubmitting(true);
      await removePermissionFromRole(role.id, permission.id);
      
      // Update local state
      setRolePermissions(prev => prev.filter(p => p.id !== permission.id));
      setAvailablePermissions(prev => [...prev, permission]);
      setHasChanges(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove permission');
      console.error('Error removing permission:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemovePermissionsBulk = async (permissions: Permission[]) => {
    if (!permissions || permissions.length === 0) return;
    try {
      setIsSubmitting(true);
      const ids = permissions.map(p => p.id);
      const res = await removePermissionsFromRoleBulk(role.id, ids);
      const removedIds = new Set(ids);
      setRolePermissions(prev => prev.filter(p => !removedIds.has(p.id)));
      // Recompute available by adding back removed permissions
      setAvailablePermissions(prev => {
        const map = new Map(prev.map(p => [p.id, p]));
        permissions.forEach(p => map.set(p.id, p));
        return Array.from(map.values());
      });
      setHasChanges(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove permissions');
      console.error('Error removing permissions bulk:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    onClose(hasChanges);
  };
  
  // Organize permissions into a tree structure
  const getPermissionTree = (permissions: Permission[], mode: 'add' | 'remove'): PermissionTreeNode[] => {
    // Group permissions by application
    const permsByApp: Record<string, Permission[]> = {};
    
    permissions.forEach(perm => {
      const appId = perm.application_id;
      if (!permsByApp[appId]) {
        permsByApp[appId] = [];
      }
      permsByApp[appId].push(perm);
    });
    
    // Create tree nodes
    return applications.map(app => {
      const appPerms = permsByApp[app.id] || [];
      
      // Group by category within each application
      const categories: Record<string, Permission[]> = {};
      appPerms.forEach(perm => {
        if (!categories[perm.category]) {
          categories[perm.category] = [];
        }
        categories[perm.category].push(perm);
      });
      
      // Create category nodes with permission children
      const categoryNodes: PermissionTreeNode[] = Object.keys(categories).sort().map(category => {
        return {
          id: `${app.id}-${category}`,
          name: category,
          type: 'category',
          icon: <FolderKanban size={16} className="text-purple-400" />,
          children: categories[category].map(perm => ({
            id: perm.id,
            name: perm.name,
            type: 'permission',
            data: perm
          }))
        };
      });
      
      // Only include applications that have permissions
      if (categoryNodes.length === 0) return null;
      
      return {
        id: app.id,
        name: app.name,
        type: 'application',
        icon: <Layers size={16} className="text-blue-400" />,
        children: categoryNodes
      };
    }).filter(Boolean) as PermissionTreeNode[];
  };
  
  const availablePermissionsTree = getPermissionTree(availablePermissions, 'add');
  const rolePermissionsTree = getPermissionTree(rolePermissions, 'remove');
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-lg shadow-xl w-full max-w-6xl overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-800 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-100">
            Manage Permissions for Role: {role.name}
          </h2>
        </div>
        
        {/* Content */}
        <div className="flex flex-col h-full max-h-[calc(100vh-150px)]">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-500 text-red-300 rounded">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Available Permissions */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-200">Available Permissions</h3>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      placeholder="Search permissions..."
                      className="pl-8 pr-4 py-1 border rounded bg-gray-800 border-gray-700 text-gray-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : availablePermissions.length === 0 ? (
                  <div className="text-center py-8 border rounded bg-gray-800/50 border-gray-700">
                    <p className="text-gray-400">No available permissions found.</p>
                  </div>
                ) : (
                  <PermissionTreeView
                    nodes={availablePermissionsTree}
                    onAddPermission={handleAddPermission}
                    onRemovePermission={() => {}}
                    onAddPermissionsBulk={handleAddPermissionsBulk}
                    disabled={isSubmitting || role.is_system_role}
                    mode="add"
                    searchTerm={searchTerm}
                  />
                )}
              </div>
              
              {/* Role Permissions */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-200">Role Permissions</h3>
                  <Badge variant="secondary">
                    {rolePermissions.length} permissions
                  </Badge>
                </div>
                
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : rolePermissions.length === 0 ? (
                  <div className="text-center py-8 border rounded bg-gray-800/50 border-gray-700">
                    <p className="text-gray-400">This role has no permissions assigned.</p>
                  </div>
                ) : (
                  <PermissionTreeView
                    nodes={rolePermissionsTree}
                    onAddPermission={() => {}}
                    onRemovePermission={handleRemovePermission}
                    onRemovePermissionsBulk={handleRemovePermissionsBulk}
                    disabled={isSubmitting || role.is_system_role}
                    mode="remove"
                    searchTerm={searchTerm}
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Footer with buttons */}
          <div className="border-t border-gray-800 px-6 py-4 bg-gray-900 flex justify-end">
            <Button onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolePermissions; 